
import type { PropOptions } from 'vue';
import Vue from 'vue';
import { getMonth, getYear } from 'date-fns';
import { validationMixin } from 'vuelidate';
import { required, requiredIf } from 'vuelidate/lib/validators';
import type { SelectableOption } from '@/components/partials/public-portfolio/PortfolioJobsInquirySection/PortfolioJobsInquirySectionProfile/types';
import CompaniesAutocompleteV2 from '~/components/common/CompaniesAutocompleteV2/CompaniesAutocompleteV2.vue';
import BravadoCheckboxV3 from '~/components/common/BravadoCheckboxV3/BravadoCheckboxV3.vue';
import BravadoSelectV3 from '~/components/common/BravadoSelectV3/BravadoSelectV3.vue';
import BravadoTextInputV3 from '~/components/common/BravadoTextInputV3/BravadoTextInputV3.vue';
import { MONTH_NAMES } from '~/constants/date';
import { validateNumberInput } from '~/components/utils/keys';
import type { FormState } from '~/types/common';

type PublicPortfolioAddWorkHistoryModal = {
  months: SelectableOption[];
  form: {
    startMonth: string;
    startYear: string;
    endMonth: string;
    endYear: string;
    company: {
      id: null | number;
      name: string;
    };
    title: string;
    isCurrentWorkplace: boolean;
  };
};

function prepareMonthAndYear(date: string): { month: string; year: string } {
  let year = '';
  let month = MONTH_NAMES[0];

  if (date) {
    const dateObj = new Date(date);

    year = String(getYear(dateObj)) || '';
    month = MONTH_NAMES[getMonth(dateObj)];
  }

  return {
    month,
    year,
  };
}

export default Vue.extend({
  name: 'PublicPortfolioWorkHistoryForm',
  components: {
    CompaniesAutocompleteV2,
    BravadoCheckboxV3,
    BravadoSelectV3,
    BravadoTextInputV3,
  },
  mixins: [validationMixin],
  props: {
    startDate: {
      type: String,
      default: '',
    } as PropOptions<string>,
    endDate: {
      type: String,
      default: '',
    } as PropOptions<string>,
    company: {
      type: Object,
      default: () => ({ id: null, name: '' }),
    } as PropOptions<{ id: null | number; name: string }>,
    title: {
      type: String,
      default: '',
    } as PropOptions<string>,
    isCurrentWorkplace: {
      type: Boolean,
      default: false,
    } as PropOptions<boolean>,
    formState: {
      type: String,
      default: 'idle',
    } as PropOptions<FormState>,
  },
  data(): PublicPortfolioAddWorkHistoryModal {
    const { month: startMonth, year: startYear } = prepareMonthAndYear(this.startDate);
    const { month: endMonth, year: endYear } = prepareMonthAndYear(this.endDate);

    return {
      months: MONTH_NAMES.map(month => ({ label: month, value: month })),
      form: {
        startMonth,
        startYear,
        endMonth,
        endYear,
        company: this.company,
        title: this.title,
        isCurrentWorkplace: this.isCurrentWorkplace,
      },
    };
  },
  validations: {
    form: {
      company: {
        name: { required },
      },
      title: {
        required,
      },
      startMonth: {
        required,
      },
      startYear: {
        required,
      },
      endMonth: {
        required: requiredIf(function (model) {
          return !model.isCurrentWorkplace;
        }),
      },
      endYear: {
        required: requiredIf(function (model) {
          return !model.isCurrentWorkplace;
        }),
      },
    },
  },
  methods: {
    validateYearInput(event: KeyboardEvent): void {
      validateNumberInput(event);
    },
    onCompanyChange(autocompleteItem: { id: number; name: string } | { text: string; value: string }): void {
      if ('name' in autocompleteItem) {
        this.form.company = autocompleteItem;
      } else if (!this.form.company.id) {
        this.form.company = { id: null, name: autocompleteItem.value };
      }
    },
  },
});
